import { useIsMobileHook } from '@teliaee/telia-common-components';
import { TranslationsContext } from '@teliaee/telia-translate';
import { inject, observer } from 'mobx-react';
import React, { useContext } from 'react';
import { EqualHeightProvider, Grid, GridColumn, Separator, Heading, Card, CardContent, List, ListItem, THeadingLevel } from 'telia-front-react';
import Config from '../../Config';
import GaPushOnMount from '../../content/GaPushOnMount';
import PersonalOffersStore from '../../stores/PersonalOffersStore';
import { OfferActions } from '../OfferInterfaces';
import B2BOfferInternetCard from './offer/B2BOfferInternetCard';
import B2BOfferMobileCard from './offer/B2BOfferMobileCard';
import GaDataStore from '../../stores/GaDataStore';
import B2BLoyaltyDiscountCodeCard from './loyalty/B2BLoyaltyDiscountCodeCard';
import B2BLoyaltyItSupportCard from './loyalty/B2BLoyaltyItSupportCard';
import B2BLoyaltyLeaseCard from './loyalty/B2BLoyaltyLeaseCard';

interface Props {
  personalOffersStore?: PersonalOffersStore;
  b2bInternetFormName: string;
  b2bMobileFormName: string;
  salesCampaignId: number;
  offerActions?: OfferActions;
  headingLevel?: THeadingLevel;
}

const B2BOfferContainer = (props: Props): JSX.Element => {
  const { translate } = useContext(TranslationsContext);
  const { personalOffersStore, b2bMobileFormName, b2bInternetFormName, salesCampaignId, offerActions, headingLevel } = props;
  const offerData = personalOffersStore.b2bPersonalOffer?.data;
  const { isMobile } = useIsMobileHook();

  const getAnalyticsProfile = (): string => {
    const getGaProfileLabel = (id: number | string) => `Profile ${id}`;
    const { offerMobile, offerInternet } = offerData;

    switch (true) {
      case offerMobile && offerInternet:
        return getGaProfileLabel(1);
      case offerMobile:
        return getGaProfileLabel(2);
      case offerInternet:
        return getGaProfileLabel(3);
      default:
        return getGaProfileLabel(4);
    }
  };

  React.useEffect(() => {
    offerData && personalOffersStore.presentSfOffers(offerData.offerIds);
  }, []);

  const mobileOffer = (): JSX.Element => {
    return (
      <GridColumn width={['lg-6']}>
        <B2BOfferMobileCard missing={!offerData.offerMobile} formName={b2bMobileFormName} headingLevel={headingLevel} />
      </GridColumn>
    );
  };

  const internetOffer = (): JSX.Element => {
    return (
      <GridColumn width={['lg-6']}>
        <B2BOfferInternetCard missing={!offerData.offerInternet} formName={b2bInternetFormName} headingLevel={headingLevel} />
      </GridColumn>
    );
  };

  const renderOffersDescription = () => {
    if (offerData.loyalty) {
      return (
        <>
          <p className="h5">{translate('personalOffers.b2b.introduction.loyalty')}</p>
          <Separator spacing="small" color="invisible" />
          <p className="h5">
            {translate('personalOffers.b2b.terms', {
              baseUrl: Config.teliaWebUrl,
            })}
          </p>
        </>
      );
    }

    return (
      <>
        <p className="h5">{translate(`personalOffers.b2b.introduction.${!offerData.offerMobile ? 'mobileMissing' : 'internetMissing'}`)}</p>
        <Separator spacing="xxsmall" color="invisible" />
        <List type="icon" icon="#check">
          {['personalOffers.b2b.discounts', 'personalOffers.b2b.cheapest.leasing', 'personalOffers.b2b.it.support'].map((perk, key) => (
            <ListItem className="text-bold" key={`perk-${key}`}>
              {translate(perk)}
            </ListItem>
          ))}
        </List>
      </>
    );
  };

  const renderOffers = () => {
    if (offerData.loyalty) {
      return (
        <>
          <GridColumn width={['lg-4']}>
            <B2BLoyaltyDiscountCodeCard
              recommendationItemId={offerData.discountCodeOfferId}
              salesCampaignId={salesCampaignId}
              offerActions={offerActions}
              headingLevel={headingLevel}
            />
          </GridColumn>
          <GridColumn width={['lg-4']}>
            <B2BLoyaltyItSupportCard headingLevel={headingLevel} />
          </GridColumn>
          <GridColumn width={['lg-4']}>
            <B2BLoyaltyLeaseCard headingLevel={headingLevel} />
          </GridColumn>
        </>
      );
    }
    if (!offerData.offerInternet) {
      return (
        <>
          {internetOffer()}
          {mobileOffer()}
        </>
      );
    }
    return (
      <>
        {mobileOffer()}
        {internetOffer()}
      </>
    );
  };

  return (
    <GaPushOnMount
      data={{
        eventLabel: getAnalyticsProfile(),
        eventAction: 'Show - Telia äriklass offer',
      }}
    >
      <Card
        theme="dark"
        isClosable={!offerData.loyalty}
        onClickClose={() => {
          personalOffersStore.rejectB2BOffers();
          GaDataStore.pushWithoutCmsData({
            event: 'AutoEvent',
            eventCategory: 'Pakkumised',
            eventAction: 'Personal offers',
            eventLabel: 'Link - Decline - Telia äriklass offer',
          });
        }}
        borderRadius={isMobile ? 'small' : 'large'}
        backgroundImage={{
          srcset:
            '//res.cloudinary.com/telia/image/upload/f_auto,fl_lossy,q_auto:best/tweb/Telia_Simplified_Deep_Purple_RGB_01_mob.png 340w, //res.cloudinary.com/telia/image/upload/f_auto,fl_lossy,q_auto:best/tweb/Telia_Simplified_Deep_Purple_RGB_01_mob.png 680w',
          role: 'presentation',
          sources: [
            {
              minWidth: '800px',
              srcset:
                '//res.cloudinary.com/telia/image/upload/f_auto,fl_lossy,q_auto:best/tweb/Telia_Simplified_Deep_Purple_RGB_01.png 800w, //res.cloudinary.com/telia/image/upload/f_auto,fl_lossy,q_auto:best/tweb/Telia_Simplified_Deep_Purple_RGB_01.png 1600w',
            },
            {
              minWidth: '1160px',
              srcset:
                '//res.cloudinary.com/telia/image/upload/f_auto,fl_lossy,q_auto:best/tweb/Telia_Simplified_Deep_Purple_RGB_01.png 1160w, //res.cloudinary.com/telia/image/upload/f_auto,fl_lossy,q_auto:best/tweb/Telia_Simplified_Deep_Purple_RGB_01.png 2320w',
            },
          ],
        }}
      >
        <CardContent padding={isMobile ? 'small' : 'large'}>
          <EqualHeightProvider groups={['heading', 'content', 'cta-button']}>
            <Grid>
              <GridColumn width={[offerData.loyalty ? 'xl-3' : 'xl-4', 'lg-12']}>
                <Card background="transparent" className="text-white" padding="none">
                  <CardContent>
                    <Heading level={headingLevel - 1} className="h3">
                      {translate(`personalOffers.b2b.title${offerData.loyalty ? '.loyalty' : ''}`)}
                    </Heading>
                    <Separator spacing="xsmall" color="invisible" />
                    {renderOffersDescription()}
                  </CardContent>
                </Card>
              </GridColumn>
              <GridColumn width={[offerData.loyalty ? 'xl-9' : 'xl-8', 'lg-12']}>
                <Grid equalHeight={true} theme="light">
                  {renderOffers()}
                </Grid>
              </GridColumn>
            </Grid>
          </EqualHeightProvider>
        </CardContent>
      </Card>
    </GaPushOnMount>
  );
};

export default inject('personalOffersStore')(observer(B2BOfferContainer));
